/**
 * Purchase-country resolution for receipts.
 *
 * - Android stores ISO-3166 alpha-2 in receipt.countryCode (e.g. "DE").
 * - iOS leaves countryCode empty; the country lives in the decoded transaction's
 *   `storefront` field as ISO-3166 alpha-3 (e.g. "DEU"), so we map alpha-3 -> alpha-2.
 */

// Minimal alpha-3 -> alpha-2 map covering the storefronts that actually appear in
// our data plus common ones. Unknown codes fall back to the alpha-3 string itself.
const ALPHA3_TO_ALPHA2: Record<string, string> = {
    DEU: "DE", NLD: "NL", USA: "US", FRA: "FR", ITA: "IT", ESP: "ES",
    POL: "PL", CHE: "CH", GBR: "GB", BRA: "BR", ARG: "AR", AUS: "AU",
    ISR: "IL", BEL: "BE", AUT: "AT", GRC: "GR", CAN: "CA", SWE: "SE",
    ROU: "RO", PRT: "PT", DNK: "DK", NOR: "NO", FIN: "FI", IRL: "IE",
    MEX: "MX", CHL: "CL", ZAF: "ZA", NZL: "NZ", JPN: "JP", KOR: "KR",
    CHN: "CN", IND: "IN", RUS: "RU", TUR: "TR", UKR: "UA", CZE: "CZ",
    HRV: "HR", HUN: "HU", THA: "TH", IDN: "ID", PHL: "PH", VNM: "VN",
    EGY: "EG", MAR: "MA", ARE: "AE", SAU: "SA", COL: "CO", PER: "PE",
    SVN: "SI", SVK: "SK", BGR: "BG", LTU: "LT", LVA: "LV", EST: "EE",
    ISL: "IS", LUX: "LU", CYP: "CY", MLT: "MT", HKG: "HK", TWN: "TW",
    SGP: "SG", MYS: "MY",
};

const ALPHA2_NAME: Record<string, string> = {
    DE: "Germany", NL: "Netherlands", US: "United States", FR: "France",
    IT: "Italy", ES: "Spain", PL: "Poland", CH: "Switzerland",
    GB: "United Kingdom", BR: "Brazil", AR: "Argentina", AU: "Australia",
    IL: "Israel", BE: "Belgium", AT: "Austria", GR: "Greece", CA: "Canada",
    SE: "Sweden", RO: "Romania", PT: "Portugal", DK: "Denmark", NO: "Norway",
    FI: "Finland", IE: "Ireland", MX: "Mexico", CL: "Chile", ZA: "South Africa",
    NZ: "New Zealand", JP: "Japan", KR: "South Korea", CN: "China", IN: "India",
    RU: "Russia", TR: "Türkiye", UA: "Ukraine", CZ: "Czechia", HR: "Croatia",
    HU: "Hungary", TH: "Thailand", ID: "Indonesia", PH: "Philippines",
    VN: "Vietnam", EG: "Egypt", MA: "Morocco", AE: "UAE", SA: "Saudi Arabia",
    CO: "Colombia", PE: "Peru", SI: "Slovenia", SK: "Slovakia", BG: "Bulgaria",
    LT: "Lithuania", LV: "Latvia", EE: "Estonia", IS: "Iceland", LU: "Luxembourg",
    CY: "Cyprus", MT: "Malta", HK: "Hong Kong", TW: "Taiwan", SG: "Singapore",
    MY: "Malaysia",
};

/** Convert an ISO alpha-2 country code into its flag emoji (regional indicators). */
function flagEmoji(alpha2: string): string {
    if (!/^[A-Za-z]{2}$/.test(alpha2)) return "";
    const A = 0x1f1e6;
    const up = alpha2.toUpperCase();
    return String.fromCodePoint(A + (up.charCodeAt(0) - 65)) + String.fromCodePoint(A + (up.charCodeAt(1) - 65));
}

export interface CountryInfo {
    /** ISO alpha-2 (best effort). */
    alpha2: string;
    /** Full country name when known, else the code. */
    name: string;
    /** Flag emoji, or "" when not derivable. */
    flag: string;
}

/**
 * Normalize whatever country signal we have (alpha-2 from Android, alpha-3
 * storefront from iOS) into a display-ready CountryInfo. Returns null when no
 * country signal is present.
 */
export function resolveCountry(raw: string | null | undefined): CountryInfo | null {
    if (!raw) return null;
    const code = raw.trim().toUpperCase();
    if (!code) return null;
    const alpha2 = code.length === 3 ? ALPHA3_TO_ALPHA2[code] ?? code : code;
    return {
        alpha2,
        name: ALPHA2_NAME[alpha2] ?? alpha2,
        flag: alpha2.length === 2 ? flagEmoji(alpha2) : "",
    };
}
